import Image from 'next/image'
import { useDropzone } from 'react-dropzone'
import { toast } from 'sonner'
import { Film, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Muted, Small, XSmall } from '@/components/ui/typography'
import {
    IMAGE_MAX_SIZE_BYTES,
    IMAGE_MAX_SIZE_LABEL,
    VIDEO_MAX_SIZE_BYTES,
    VIDEO_MAX_SIZE_LABEL,
} from '@/lib/uploadLimits'
import {
    MAX_ASSETS,
    type ManualCreativeFormVariant,
    type ManualCreativeUploadFormState,
} from './useManualCreativeUploadForm'

interface ManualCreativeUploadFormProps {
    form: ManualCreativeUploadFormState
    variant?: ManualCreativeFormVariant
    disabled?: boolean
}

export function ManualCreativeUploadForm({
    form,
    variant = 'paid',
    disabled = false,
}: ManualCreativeUploadFormProps) {
    const {
        files,
        adCopy,
        setAdCopy,
        productUrl,
        setProductUrl,
        creativeName,
        setCreativeName,
        isUploading,
        videoUpload,
        addFiles,
        removeFile,
    } = form
    const isEmail = variant === 'email'
    const copyLabel = isEmail ? 'Message Copy Text' : 'Ad Copy Text'
    const copyPlaceholder = isEmail
        ? 'Enter the message copy text...'
        : 'Enter the ad copy text...'

    const isAtMax = files.length >= MAX_ASSETS
    const inputsDisabled = disabled || isUploading

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: addFiles,
        onDropRejected: (fileRejections) => {
            const [rejection] = fileRejections
            const fileName = rejection?.file?.name
            const errorCodes =
                rejection?.errors.map((error) => error.code) || []
            const isInvalidType = errorCodes.includes('file-invalid-type')
            const isTooLarge = errorCodes.includes('file-too-large')
            const isVideo = rejection?.file?.type?.startsWith('video/')
            const limitLabel = isVideo
                ? VIDEO_MAX_SIZE_LABEL
                : IMAGE_MAX_SIZE_LABEL
            toast.error(
                isInvalidType
                    ? 'Unsupported file type. Please upload an image or video.'
                    : isTooLarge && fileName
                      ? `"${fileName}" exceeds the ${limitLabel} limit`
                      : 'Creative upload error. Please try again or a different creative.',
            )
        },
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
            'video/*': ['.mp4', '.mov', '.avi'],
        },
        validator: (file) => {
            const isVideo = file.type.startsWith('video/')
            const max = isVideo ? VIDEO_MAX_SIZE_BYTES : IMAGE_MAX_SIZE_BYTES
            if (file.size > max) {
                return {
                    code: 'file-too-large',
                    message: `File exceeds ${isVideo ? VIDEO_MAX_SIZE_LABEL : IMAGE_MAX_SIZE_LABEL} limit`,
                }
            }
            return null
        },
        multiple: true,
        disabled: inputsDisabled || isAtMax,
    })

    return (
        <div className="space-y-4">
            <div>
                <div className="mb-1 flex items-center justify-between">
                    <Label>Creative Files</Label>
                    {files.length > 0 && (
                        <XSmall className="text-muted-foreground">
                            {files.length} / {MAX_ASSETS} assets
                        </XSmall>
                    )}
                </div>

                {files.length > 0 && (
                    <div className="mb-3 space-y-2">
                        {files.map((f) => (
                            <div
                                key={f.id}
                                className="flex items-center gap-3 rounded-lg border p-2"
                            >
                                <div className="size-12 shrink-0 overflow-hidden rounded">
                                    {f.type === 'IMAGE' ? (
                                        <Image
                                            src={f.previewUrl}
                                            alt={f.file.name}
                                            width={48}
                                            height={48}
                                            className="size-full object-cover"
                                            unoptimized
                                        />
                                    ) : (
                                        <div className="flex size-full items-center justify-center bg-muted">
                                            <Film className="size-5 text-muted-foreground" />
                                        </div>
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <Small className="block truncate font-medium">
                                        {f.file.name}
                                    </Small>
                                    <XSmall className="mt-1 block text-muted-foreground">
                                        {f.type === 'IMAGE' ? 'Image' : 'Video'}
                                    </XSmall>
                                </div>

                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="size-8 shrink-0 p-0"
                                    onClick={() => removeFile(f.id)}
                                    disabled={inputsDisabled}
                                >
                                    <X className="size-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {!isAtMax && (
                    <div
                        {...getRootProps()}
                        className={`cursor-pointer rounded-lg border-2 border-dashed text-center transition-colors ${
                            isDragActive
                                ? 'bg-primary/5 border-primary'
                                : 'hover:border-primary/50 border-border'
                        } ${files.length > 0 ? 'p-4' : 'p-8'}`}
                    >
                        <input {...getInputProps()} />
                        <Upload
                            className={`mx-auto text-muted-foreground ${
                                files.length > 0
                                    ? 'mb-2 size-6'
                                    : 'mb-4 size-12'
                            }`}
                        />
                        <Muted className="text-center text-muted-foreground">
                            {isDragActive
                                ? 'Drop your files here'
                                : files.length > 0
                                  ? `Add more files (${MAX_ASSETS - files.length} remaining)`
                                  : 'Drag & drop images or videos, or click to browse'}
                        </Muted>
                        {files.length === 0 && (
                            <XSmall className="mt-2 text-center text-muted-foreground">
                                Supports: PNG, JPG, WebP, MP4, MOV · Max{' '}
                                {MAX_ASSETS} files
                            </XSmall>
                        )}
                    </div>
                )}

                {isAtMax && (
                    <XSmall className="mt-1 text-center text-muted-foreground">
                        Maximum {MAX_ASSETS} files reached
                    </XSmall>
                )}
            </div>

            <div>
                <Label htmlFor="adCopy">{copyLabel}</Label>
                <Textarea
                    id="adCopy"
                    value={adCopy}
                    onChange={(e) => setAdCopy(e.target.value)}
                    placeholder={copyPlaceholder}
                    rows={4}
                    className="mt-2"
                    disabled={inputsDisabled}
                />
            </div>

            <div>
                <Label htmlFor="productUrl">Current Landing Page URL</Label>
                <Input
                    id="productUrl"
                    type="url"
                    value={productUrl}
                    onChange={(e) => setProductUrl(e.target.value)}
                    placeholder="https://your-store.com/products/..."
                    className="mt-2"
                    disabled={inputsDisabled}
                />
            </div>

            <div>
                <Label htmlFor="creativeName">Creative Name (optional)</Label>
                <Input
                    id="creativeName"
                    value={creativeName}
                    onChange={(e) => setCreativeName(e.target.value)}
                    placeholder="e.g., Summer Sale Banner"
                    className="mt-2"
                    disabled={inputsDisabled}
                />
            </div>

            {videoUpload && (
                <div className="space-y-2">
                    <Muted>
                        {videoUpload.label}...{' '}
                        {Math.round(videoUpload.progress)}%
                    </Muted>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                            className="h-full transition-all bg-info"
                            style={{ width: `${videoUpload.progress}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
